// ── Furniture drag session ──
// One pointerdown on a furniture sprite, from the gates that decide whether it is a drag at
// all to the stage handlers that carry it to completion. The session reads top-to-bottom as
// its phases; each phase lives in a module named after what it does:
//
//   dragStart.resolveDragTarget()        can we drag, and what are the live field values?
//   dragStart.collectDragScope()         per-drag tile maps + carried items
//   dragStart.captureSurfaceOrigin()     where the item started (floor-vs-surface at drop)
//   dragStart.bootstrapWindowSnap()      the wall a wall-mounted item already hangs on
//   dragMove.handleDragPointerMove()     every frame of the drag
//   dragUp.handleDragPointerUp()         the drop, and the release that always runs
//
// What stays HERE is what the phases share: the two snap closures that must read live
// values at call time, and the subscribe/unsubscribe of the handlers.
//
// There is no long-press: holding an item only drags it. The action menu opens on a
// quick tap (dragUp, a pointer-up with no drag) and its options are tapped.
//
// ── Freshness ──
// `getRoomContainer` / `getRoomViewport` / `getLiveCatalog` are passed as FUNCTIONS (not
// pre-read values), so every call site keeps read-at-invocation-time semantics — including
// the one inside the async pointer-up path, which reads `getRoomViewport()` before its first
// `await` exactly as before. No value is hoisted across an `await`.
// `rawEffectiveWidthTiles` / `rawEffectiveHeightTiles` / `rugOverflowNormal` /
// `rugOverflowMirrored` are outer `let`s in the caller, but they are assigned once during
// setupFurnitureInteraction — before any pointer handler can run — and never again, so they
// are passed by value.
//
// ── Lifetime of the three drag maps ──
// They live in `scope`, i.e. one object per pointerdown, and the pointer-up `finally` resets
// them through `resetDragMaps` exactly where the original reassigned its `let`s. Within a
// single drag the behaviour is identical: they are built before any reader runs
// (snapAnchorToGrid is only reachable from pointermove; the drop commit and the reset both
// run after that) — which is why the original had to reset them "for next drag" at all.
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { QueryClient } from '@tanstack/react-query';
import { handleDragPointerMove } from './dragMove';
import { handleDragPointerUp } from './dragUp';
import { TOUCH_HOLD_PICKUP_MS } from '../../../domain/touchLift';
import { edgeScrollVelocity } from '../../../domain/edgeAutoScroll';
import { holdCameraForDrag, isExtraFinger, restoreFacing } from './cameraGestures';

const isTouchPointer = (pointerType: string | undefined) => pointerType === 'touch' || pointerType === 'pen';

/** How far (room-local px) edge auto-scroll may take the view centre from the room's origin. */
const EDGE_PAN_LIMIT = { x0: -650, x1: 650, y0: -350, y1: 750 };
import {
  resolveDragTarget,
  collectDragScope,
  captureSurfaceOrigin,
  bootstrapWindowSnap,
} from './dragStart';
import type { RenderFurniture } from '../../../domain/types';
import type {
  RoomSceneDragState,
  RoomSceneFurniture,
  RoomSceneDrag,
  RoomSceneOverlays,
  RoomSceneFlags,
  RoomSceneAdmin,
} from '../state/roomSceneState';
import {
  snapWindowAnchorToGrid as snapWindowAnchorToGridImported,
  type WindowSnapResult,
} from '../../../domain/windowSnap';

/**
 * Shape of `drag.state`. Owned by this module (the drag session is the only
 * writer); re-exported by useFurnitureInteraction as `RoomDragState` for its consumers.
 */
export interface RoomDragState {
  isDragging: boolean;
  interiorId: number;
  marker: PIXI.Container;
  startX: number;
  startY: number;
  startWorldX: number; // Initial world position of cursor when drag started
  startWorldY: number; // Initial world position of cursor when drag started
  offsetX: number;
  offsetY: number;
  origX: number;
  origY: number;
  previousWindowSnap?: WindowSnapResult; // Last correct snap for wall hysteresis
}

export interface DragSessionContext {
  scene: DragSessionScene;
  refs: DragSessionRefs;
  item: DragSessionItem;
  geometry: DragSessionGeometry;
  radialMenu: DragSessionRadialMenu;
  selectionOutline: DragSessionSelectionOutline;
  gates: DragSessionGates;
  services: DragSessionServices;
  previewActions: DragSessionPreviewActions;
}

export interface DragSessionScene {
  getRoomContainer: () => PIXI.Container | null;
  getRoomViewport: () => Viewport | null;
  /** `mapSceneSingleton.app`, read once in setupFurnitureInteraction as in the original. */
  app: PIXI.Application | null;
}

export interface DragSessionRefs {
  furniture: RoomSceneFurniture;
  drag: RoomSceneDrag;
  overlays: RoomSceneOverlays;
  flags: RoomSceneFlags;
  admin: RoomSceneAdmin;
}

export interface DragSessionItem {
  sprite: PIXI.Sprite;
  furniture: RenderFurniture;
  markerContainer: PIXI.Container;
}

export interface DragSessionGeometry {
  tileDimW: number | null | undefined;
  tileDimH: number | null | undefined;
  interactionIsRug: boolean;
  isWindow: boolean;
  rawEffectiveWidthTiles: number;
  rawEffectiveHeightTiles: number;
  /** Live catalog read at call time so admin tile edits are picked up (see original). */
  getLiveCatalog: () => RenderFurniture['furniture_catalog'] | undefined;
}

export interface DragSessionRadialMenu {
  openFurnitureRadialMenu: (marker: PIXI.Container, furniture: RenderFurniture, pointerX: number, pointerY: number) => void;
}

export interface DragSessionSelectionOutline {
  showGreenOutline: (marker: PIXI.Container) => void;
  removeGreenOutline: () => void;
  logFurnitureDiagnostics: () => void;
}

export interface DragSessionGates {
  canManageFurniture: boolean;
  houseFurnitureLoaded: boolean;
  houseFurnitureFetching: boolean;
}

export interface DragSessionServices {
  mapObjectId: number;
  queryClient: QueryClient;
  t: (key: string) => string;
}

export interface DragSessionPreviewActions {
  updatePreviewPosition: (instanceId: number, x: number, y: number) => void;
  updatePreviewPlacementMode: (instanceId: number, placement_mode: 'floor' | 'surface') => void;
  togglePreviewMirror: (instanceId: number) => void;
}

/**
 * Runs one furniture drag session: everything the original `onPointerDown` did,
 * including attaching the globalpointermove/pointerup handlers to `app.stage`.
 */
export function beginFurnitureDragSession(
  event: PIXI.FederatedPointerEvent,
  ctx: DragSessionContext,
): void {
  const {
    scene: { app },
    refs: { drag },
    item: { furniture, markerContainer },
    geometry: {
      rawEffectiveWidthTiles,
      rawEffectiveHeightTiles,
    },
    selectionOutline: { removeGreenOutline },
  } = ctx;

  const target = resolveDragTarget(event, ctx);
  if (!target) return;
  const { roomContainer, roomViewport, livePlacementMode, liveWallFloorOffset } = target;

  // A finger that lands while another is already down is a camera gesture (pinch /
  // two-finger pan) — it never grabs furniture.
  if (isExtraFinger(event)) return;

  // Outside build mode furniture is not editable at all: no drag and no
  // tap menu (a tap only does item-specific things such as playing a piano,
  // handled elsewhere).
  if (!ctx.refs.flags.buildMode) return;

  const scope = collectDragScope(ctx);
  const origin = captureSurfaceOrigin(ctx, livePlacementMode, scope.dragSurfaceMap);

  // ── Window-specific snap: auto-mirror to face nearest wall + magnetic snap ──
  // cursorWorldX: optional raw cursor X position for wall selection during drag
  // previousSnap: optional previous snap result for distance preservation on wall transition
  const snapWindowAnchorToGrid = (anchorX: number, anchorY: number, cursorWorldX?: number, previousSnap?: WindowSnapResult) => {
    // continuous: while dragging, wall items slide along the wall instead of stepping.
    return snapWindowAnchorToGridImported(anchorX, anchorY, rawEffectiveWidthTiles, rawEffectiveHeightTiles, cursorWorldX, previousSnap, true);
  };

  const worldPos = roomContainer.toLocal(event.global);

  // The camera stops following this finger, but keeps tracking touches so that a
  // second finger still reaches the pinch (cameraGestures).
  const releaseCamera = holdCameraForDrag(roomViewport);

  const offsetX = worldPos.x - markerContainer.position.x;
  const offsetY = worldPos.y - markerContainer.position.y;

  const previousWindowSnap = bootstrapWindowSnap(ctx, liveWallFloorOffset);

  // Suspend auto-capture for the whole gesture: a surface drag issues N+1
  // sequential cache writes (table + each carried item), each of which would
  // otherwise push its own undo revision. We record exactly one final revision
  // in the pointer-up handler's finally. Cleared there on every exit path.
  drag.captureSuspended = true;

  const session: RoomSceneDragState = {
    isDragging: false,
    interiorId: furniture.id,
    marker: markerContainer,
    startX: event.global.x,
    startY: event.global.y,
    pointerType: event.pointerType,
    startWorldX: worldPos.x, // Initial world position of cursor when drag started
    startWorldY: worldPos.y, // Initial world position of cursor when drag started
    offsetX,
    offsetY,
    origX: markerContainer.position.x,
    origY: markerContainer.position.y,
    previousWindowSnap,
  };
  drag.state = session;

  // Where the finger is now — the pick-up and the lift animation re-run the move
  // handler from here when the finger itself is not moving.
  let lastGlobal = { x: event.global.x, y: event.global.y };
  const pointerId = event.pointerId; // read now: PIXI recycles federated events
  const startMirrored = ctx.item.sprite.scale.x < 0;
  let ended = false;
  const stillThisGesture = () => !ended && drag.state === session;

  // The drag maps are passed as getters so each invocation reads the current value;
  // `resetDragMaps` performs the only write the handlers make back into this scope.
  const runMove = (moveEvent: PIXI.FederatedPointerEvent) => {
    handleDragPointerMove(moveEvent, {
      scene: ctx.scene,
      refs: ctx.refs,
      item: ctx.item,
      geometry: { ...ctx.geometry, liveWallFloorOffset },
      snap: { snapWindowAnchorToGrid },
      dragMaps: {
        getDragSurfaceMap: () => scope.dragSurfaceMap,
        getDragFootprintMap: () => scope.dragFootprintMap,
        getDragSurfaceItemFootprintMap: () => scope.dragSurfaceItemFootprintMap,
      },
      carried: { carriedItems: scope.carriedItems, carriedSet: scope.carriedSet },
      services: ctx.services,
      removeGreenOutline,
    });
  };
  // Synthetic move at the last finger position (only `.global` is read).
  const rerunMove = () => runMove({ global: lastGlobal } as unknown as PIXI.FederatedPointerEvent);

  // The lift rises over time (domain/touchLift): keep re-running the move each
  // frame until it has risen, so it finishes smoothly even under a still finger.
  let liftLoopRunning = false;
  const ensureLiftLoop = () => {
    if (liftLoopRunning || !session.isDragging || (session.liftProgress ?? 1) >= 1) return;
    liftLoopRunning = true;
    const frame = () => {
      if (!stillThisGesture() || (session.liftProgress ?? 1) >= 1) { liftLoopRunning = false; return; }
      rerunMove();
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  // Edge auto-scroll: while an item is being dragged near a screen edge the camera
  // pans that way (and the item, held by the finger, travels with the view).
  let edgeLoopRunning = false;
  const ensureEdgeLoop = () => {
    if (edgeLoopRunning || !session.isDragging) return;
    edgeLoopRunning = true;
    const frame = () => {
      if (!stillThisGesture()) { edgeLoopRunning = false; return; }
      const vp = ctx.scene.getRoomViewport();
      const rc = ctx.scene.getRoomContainer();
      if (vp && rc) {
        // The item rides above the finger (touch lift): it would reach the top edge
        // and vanish long before the finger does, so the top edge goes by where the
        // item is shown; the bottom one by the finger (the item never gets there).
        const liftNow = (session.liftPx ?? 0) * (session.liftProgress ?? 0);
        const v = edgeScrollVelocity(lastGlobal.x, lastGlobal.y, vp.screenWidth, vp.screenHeight, liftNow);
        if (v.dx || v.dy) {
          // Keep the view around the room — never pan off into the void.
          const cx = Math.max(rc.position.x + EDGE_PAN_LIMIT.x0, Math.min(rc.position.x + EDGE_PAN_LIMIT.x1, vp.center.x + v.dx / vp.scale.x));
          const cy = Math.max(rc.position.y + EDGE_PAN_LIMIT.y0, Math.min(rc.position.y + EDGE_PAN_LIMIT.y1, vp.center.y + v.dy / vp.scale.y));
          if (cx !== vp.center.x || cy !== vp.center.y) {
            vp.moveCenter(cx, cy);
            rerunMove();
          }
        }
      }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  // `globalpointermove`, not `pointermove`: the latter only reaches the stage via
  // whatever is under the finger, and while an item is dragged the viewport is
  // paused, so its hit area stays where the camera was. Once the edge pan carried
  // the view past it (or over the void around the room) moves stopped arriving —
  // the item froze and the pan kept running from the last edge position.
  const onPointerMove = (moveEvent: PIXI.FederatedPointerEvent) => {
    if (moveEvent.pointerId !== pointerId) return; // a second finger (pinch)
    lastGlobal = { x: moveEvent.global.x, y: moveEvent.global.y };
    runMove(moveEvent);
    ensureLiftLoop();
    ensureEdgeLoop();
  };

  // Hold to pick up (touch): a finger resting on the item lifts it before it moves.
  // A tap is shorter and never lifts.
  const holdTimer = isTouchPointer(event.pointerType)
    ? window.setTimeout(() => {
        if (!stillThisGesture() || session.isDragging) return;
        session.pickedUp = true;
        rerunMove();
        ensureLiftLoop();
        ensureEdgeLoop();
      }, TOUCH_HOLD_PICKUP_MS)
    : null;

  const detachStageHandlers = () => {
    if (!app) return;
    app.stage.off('globalpointermove', onPointerMove);
    app.stage.off('pointerup', onFingerUp);
    app.stage.off('pointerupoutside', onFingerUp);
    app.stage.off('pointerdown', onOtherFingerDown);
  };

  // Only the finger that holds the item ends its drag.
  const onFingerUp = (upEvent: PIXI.FederatedPointerEvent) => {
    if (upEvent.pointerId === pointerId) void onPointerUp();
  };

  // A second finger: the gesture is the camera's now. The item goes back where it
  // was (nothing saved, no selection) and the viewport's pinch takes both fingers.
  const onOtherFingerDown = (downEvent: PIXI.FederatedPointerEvent) => {
    if (ended || downEvent.pointerId === pointerId || downEvent.pointerType === 'mouse') return;
    session.cancelled = true;
    restoreFacing(ctx.item.sprite, startMirrored);
    void onPointerUp();
  };

  const onPointerUp = async () => {
    ended = true;
    if (holdTimer !== null) window.clearTimeout(holdTimer);
    await handleDragPointerUp({
      session: ctx,
      live: { livePlacementMode, liveWallFloorOffset },
      gesture: {
        detachStageHandlers,
        releaseCamera,
        resetDragMaps: () => {
          scope.dragSurfaceMap = new Map();
          scope.dragFootprintMap = new Map();
          scope.dragSurfaceItemFootprintMap = new Map();
        },
      },
      scope,
      origin,
    });
  };

  if (app) {
    app.stage.on('globalpointermove', onPointerMove);
    app.stage.on('pointerup', onFingerUp);
    app.stage.on('pointerupoutside', onFingerUp);
    app.stage.on('pointerdown', onOtherFingerDown);
  }
}
